import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, ArrowRight, Youtube } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, CATEGORIES, errMsg } from "@/lib/api";
import { Logo } from "./Header";

export const Newsletter = ({ bg }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/subscribe", { email }); toast.success(t("subscribed")); setEmail(""); }
    catch (er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="newsletter-section" className="relative overflow-hidden">
      {bg && <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/80 to-[#0b0b0b]/40" />
      <div className="relative mx-auto grid max-w-[1600px] gap-10 px-5 py-24 md:grid-cols-2 md:px-10 md:py-32">
        <div>
          <h2 className="font-display text-4xl font-bold text-white sm:text-5xl">{t("joinTitle")}</h2>
          <p className="mt-4 max-w-md text-white/75">{t("joinLead")}</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3 self-end sm:flex-row">
          <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")}
            className="h-14 flex-1 border border-white/25 bg-black/40 px-5 text-sm text-white placeholder:text-white/50 outline-none backdrop-blur-md focus:border-white" />
          <button data-testid="newsletter-submit-button" disabled={busy} className="btn-line h-14 justify-center">{t("subscribe")} <ArrowRight size={14} /></button>
        </form>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t, settings } = useLang();
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#0b0b0b]">
      <div className="mx-auto grid max-w-[1600px] gap-12 px-5 py-16 md:grid-cols-12 md:px-10">
        <div className="md:col-span-4">
          <Logo className="h-12" />
          <p className="mt-6 max-w-xs text-sm text-white/60">Wildlife · Landscape · Underwater · People · Travel</p>
        </div>
        <div className="grid grid-cols-2 gap-8 text-sm md:col-span-5">
          <div className="flex flex-col gap-2.5">
            <span className="kicker mb-2">{t("gallery")}</span>
            {CATEGORIES.map((c) => <Link key={c} to={`/gallery/${c}`} data-testid={`footer-link-${c}`} className="text-white/70 hover:text-white">{t(c)}</Link>)}
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="kicker mb-2">MROUE CANVAS</span>
            {[["video", "/video"], ["frame", "/frame-visualizer"], ["blog", "/blog"], ["instagram", "/instagram"], ["about", "/about"], ["contact", "/contact"]].map(([k, to]) => (
              <Link key={k} to={to} data-testid={`footer-link-${k}`} className="text-white/70 hover:text-white">{t(k)}</Link>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4 md:col-span-3">
          <span className="kicker">{t("contact")}</span>
          <a href={`mailto:${settings.contact_email}`} data-testid="footer-email" className="text-white/80 hover:text-white">{settings.contact_email}</a>
          <div className="flex gap-4 text-white/70">
            <a href={`https://www.instagram.com/${settings.instagram_handle || ""}`} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="hover:text-white"><Instagram size={18} /></a>
            {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="hover:text-white"><Youtube size={18} /></a>}
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-[1600px] flex-col justify-between gap-2 border-t border-white/[0.06] px-5 py-6 font-mono text-[11px] tracking-wider text-white/50 sm:flex-row md:px-10">
        <span>© {new Date().getFullYear()} MROUE CANVAS · mrouecanvas.com</span>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white">Admin</Link>
      </div>
    </footer>
  );
};
